import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { from, Observable, Observer, Subject } from 'rxjs';
import { NotifierService } from 'angular-notifier';
import Cookies from 'js-cookie';
import { nanoid } from 'nanoid';
import { WebSocketMessage } from '../models/WebSocketMessage';

// The signed-out detection predicate, exported so the spec and the onmessage
// handler cannot drift: the server (session-manager denyAccess) emits the
// denial reason BOTH at top level and under data, and either position must
// fire. Matching only one of them was how the signed-out UI silently stopped
// firing for a whole release. See session-denial.spec.ts.
export function isSessionDenialFrame(data: any): boolean {
  return data?.reason === "authentication" || data?.data?.reason === "authentication";
}

@Injectable({
  providedIn: 'root'
})
export class SystemService {

  public eventEmitter: EventEmitter<any> = new EventEmitter<any>();
  public ws:WebSocket = null;

  public wsObservable: Observable<MessageEvent>;
  public wsSubject: Subject<MessageEvent>;
  private wsHealthCheckInterval:any = null;
  private wsError:boolean = false;

  // Fires when the backend reports that it can no longer identify us - the PHP
  // session behind this connection is gone or expired. UserService listens and
  // tears the signed-in state down. It lives here rather than in UserService
  // because every command shares this socket, so any of them can be the one
  // that discovers the session died; UserService cannot be injected here
  // without a circular dependency (it already depends on SystemService).
  public sessionInvalidated$: Subject<void> = new Subject<void>();
  

  constructor(private http:HttpClient, private notifierService: NotifierService) {
    this.wsSubject = new Subject<MessageEvent>();

    this.initWebSocket();
  }

  setCurrentApplication(app) {
    this.eventEmitter.emit(app);
    //set cookie
    Cookies.set('CurrentApplication', app, { domain: window.location.hostname, secure: true, sameSite: 'None' });
  }

  getCurrentApplication() {
    return Cookies.get('CurrentApplication');
  }


  async runCommandInOperationsSession(sessionAccessCode:string, cmd:string, env:Array<any> = []) {

    let msg = {
      appSession: sessionAccessCode,
      cmd: "route-to-ca",
      caCmd: cmd,
      env: env
    }

    return this.sendCommandToBackend(msg);
  }

  shutdownOperationsSession(sessionAccessCode:string) {
    if(sessionAccessCode == null || sessionAccessCode.length < 1) {
      console.log("No session to shutdown");
      return;
    }
    if(this.ws && this.ws.readyState == 1) {
      this.ws.send(JSON.stringify({ cmd: "shutdownOperationsSession",  sessionAccessCode: sessionAccessCode }));
      //this.ws.close();
    }
  }

  async sendMessageToBackend(jsonMsg, listenerObservable = null) { //try not to use this and see sendCommandToBackend instead
    if(this.ws != null) {
      this.ws.send(jsonMsg);
    }
    else {
      console.error("sendMessageToBackend failed because there's no active websocket");
    }

    if(listenerObservable) {
      this.wsSubject.subscribe(listenerObservable);
    }
    
    /*
    let ws = <WebSocket>await this.initWebSocket();
    console.log(ws);
    ws.send(jsonMsg);
    */
    return this.ws;
  }

  sendCommandToBackendObservable(command): Observable<WebSocketMessage> {
    if(!command.requestId) {
      command.requestId = nanoid();
    }

    return new Observable((observer) => {
      //hook onto the websocket and listen for the response
      let obs = this.wsSubject.subscribe({
        next: (data:any) => {
          
          let expectedCmd = command.cmd;
          if(command.cmd == "route-to-ca") {
            expectedCmd = command.caCmd;
          }

          if(typeof data.requestId == "undefined") {
            console.warn("sendCommandToBackendObservable received return-data without requestId, the command was: "+data.cmd+", expected: "+expectedCmd);
            return;
          }

          if(data.requestId === command.requestId) {
            if(data.cmd != expectedCmd) {
              console.error("sendCommandToBackendObservable received return-data from another operation, the command was: "+data.cmd+", expected: "+expectedCmd);
              observer.error();
              return;
            }

            observer.next(data);
          }

        },
        error: (err) => {
          console.error(err);
          observer.error(err);
        }
      });
  
      if(this.ws != null && this.ws.readyState == 1) {
        this.ws.send(JSON.stringify(command));
      }
      else {
        let sendAttemptInterval = setInterval(() => {
          if(this.ws.readyState == 3) { //3 == CLOSED
            this.initWebSocket();
          }
          if(this.ws != null && this.ws.readyState == 1) {
            this.ws.send(JSON.stringify(command));
            clearInterval(sendAttemptInterval);
          }
        }, 1000);
      }

    });
  }

  async sendCommandToBackend(command): Promise<WebSocketMessage> {

    if(!command.requestId) {
      command.requestId = nanoid();
    }
    
    return new Promise((resolve, reject) => {
      //hook onto the websocket and listen for the response
      let obs = this.wsSubject.subscribe({
        next: (data:any) => {
          
          let expectedCmd = command.cmd;
          if(command.cmd == "route-to-ca") {
            expectedCmd = command.caCmd;
          }

          if(typeof data.requestId == "undefined") {
            console.warn("sendCommandToBackend received return-data without requestId, the command was: "+data.cmd+", expected: "+expectedCmd);
            return;
          }

          if(data.requestId === command.requestId) {
            if(data.cmd != expectedCmd) {
              console.error("sendCommandToBackend received return-data from another operation, the command was: "+data.cmd+", expected: "+expectedCmd);
              obs.unsubscribe();
              reject();
              return;
            }

            obs.unsubscribe();
            resolve(data);
          }

        },
        error: (err) => {
          console.error(err);
          reject(err);
        }
      });
  
      if(this.ws != null && this.ws.readyState == 1) {
        this.ws.send(JSON.stringify(command));
      }
      else {
        let sendAttemptInterval = setInterval(() => {
          if(this.ws.readyState == 3) { //3 == CLOSED
            this.initWebSocket();
          }
          if(this.ws != null && this.ws.readyState == 1) {
            this.ws.send(JSON.stringify(command));
            clearInterval(sendAttemptInterval);
          }
        }, 1000);
      }
    });
  }

  async initWebSocket() {
    return new Promise<void>((resolve, reject) => {
      let webSocketProto = "wss:";
      if(window.location.protocol == "http:") {
        webSocketProto = "ws:";
      }
      const wsUrl = webSocketProto+'//'+window.location.host;
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = (event) => {
        resolve(); // Resolve the promise when the connection is open
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        //this.notifierService.notify("error", "Attempt to reconnect to backend failed.");
        reject(error); // Reject the promise on error
      };

      this.ws.onclose = (event) => {
        if (event.wasClean) {
          console.log(`WebSocket closed cleanly, code=${event.code}, reason=${event.reason}`);
        } else {
          console.warn(`WebSocket disconnected unexpectedly, code=${event.code}`);
        }
    
        // Determine who closed the connection
        if (event.code === 1000) {
          console.log("Client or server closed the connection normally.");
        } else if (event.code === 1006) {
          console.warn("Connection lost.");
        } else {
          console.log("Closed with code:", event.code);
        }
      };

      this.ws.onmessage = (event) => {
        let data = JSON.parse(event.data);

        // Watch every reply, not just the ones a caller is awaiting: a command
        // whose promise nobody handles must still be able to surface an expired
        // session. The server sends the denial reason at top level AND under
        // data (session-manager denyAccess); accept either position - asserting
        // on only one was how the signed-out UI silently stopped firing.
        // (session-denial.spec.ts pins this shape.)
        if(isSessionDenialFrame(data)) {
          this.sessionInvalidated$.next();
        }

        this.wsSubject.next(data);
      };
    });
  }

}
